import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Sparkles } from 'lucide-react';
import { changelogEntries, type ChangelogEntry } from './changelog';

type EntryType = ChangelogEntry['type'];

const TYPE_ORDER: EntryType[] = ['feature', 'improvement', 'fix', 'update'];

const typePill: Record<EntryType, string> = {
  feature: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400',
  improvement: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  fix: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400',
  update: 'bg-muted text-foreground/70',
};

const typeDot: Record<EntryType, string> = {
  feature: 'bg-amber-500',
  improvement: 'bg-blue-500',
  fix: 'bg-rose-500',
  update: 'bg-foreground/40',
};

const formatDate = (d: string) => {
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return d;
  }
};

const groupByType = (entries: ChangelogEntry[]) =>
  TYPE_ORDER.map((type) => ({
    type,
    entries: entries.filter((e) => e.type === type),
  })).filter((g) => g.entries.length > 0);

interface ChangelogDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChangelogDrawer: React.FC<ChangelogDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const groups = groupByType(changelogEntries);

  const typeLabels: Record<EntryType, string> = {
    feature: t('changelogDrawer.features'),
    improvement: t('changelogDrawer.improvements'),
    fix: t('changelogDrawer.bugfixes'),
    update: t('changelogDrawer.updates'),
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-lg">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            <SheetTitle className="text-lg">{t('common.changelog')}</SheetTitle>
          </div>
          <SheetDescription>{t('changelogDrawer.description')}</SheetDescription>
        </SheetHeader>

        <div className="mt-8 h-[calc(100vh-130px)] overflow-y-auto pr-2">
          <div className="space-y-8">
            {groups.map((group) => (
              <section key={group.type}>
                <span
                  className={`mb-4 inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${typePill[group.type]}`}
                >
                  {typeLabels[group.type]}
                </span>

                <div>
                  {group.entries.map((entry, index) => {
                    const isLast = index === group.entries.length - 1;
                    return (
                      <div key={index} className="flex gap-3">
                        {/* Timeline: dot + connecting line */}
                        <div className="flex flex-col items-center pt-1.5">
                          <span
                            className={`h-2.5 w-2.5 shrink-0 rounded-full ${typeDot[group.type]}`}
                            aria-hidden
                          />
                          {!isLast && <span className="mt-1 w-px flex-1 bg-border" />}
                        </div>

                        <div className={`min-w-0 flex-1 ${isLast ? '' : 'pb-6'}`}>
                          <h3 className="font-semibold leading-snug text-foreground">
                            {entry.title}
                          </h3>
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {formatDate(entry.date)}
                            {entry.version ? ` · ${entry.version}` : ''}
                          </div>
                          {entry.description && (
                            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                              {entry.description}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
